/** DEPENDENCIES */
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import debounce from 'lodash/debounce';
import uniqBy from 'lodash/uniqBy';

/** UTILS */
import { $get } from 'commons/js/utils/api';
import { capitalizeObject } from 'commons/js/utils/strings';

export default {
  props: {
    isCookieFirstEnabled: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      map: null,
      units: [],
      companies: [],
      company: '',
      limit: 100,
      markers: [],
      cluster: null,
      logos: {},
      position: {
        latitude: -22.0294062,
        longitude: -47.8884301,
      },
      center: null,
      loading: true,
    };
  },

  computed: {
    filteredUnits() {
      return this.units
        .filter((unit) => {
          if (!this.company) return true;

          const company = Number(this.company);
          const partner = this.company === 'partner';

          if (partner) return unit.type !== 'normal';

          return unit.companies.includes(company);
        })
        .slice(0, this.limit);
    },
  },

  watch: {
    async filteredUnits() {
      if (!this.cluster) return;

      this.cluster.removeMarkers(this.markers);

      this.markers = [];

      await this.createMarkers();

      this.cluster.addMarkers(this.markers);
    },
  },

  created() {
    window.addEventListener('google_maps_loaded', this.init);
  },

  mounted() {
    this.getConsent();
  },

  destroyed() {
    window.removeEventListener('google_maps_loaded');
  },

  methods: {
    getConsent: debounce(function() {
      if (!this.isCookieFirstEnabled) return;

      const consent = window.CookieFirst && window.CookieFirst.consent;

      if (!consent || (consent && !consent.functional)) {
        this.$refs.consent.open();
      }
    }, 1000),

    handleConsentModal() {
      try {
        this.$refs.consent.close();

        cookiefirst_show_settings();
      } catch (error) {
        console.error('Cookie First error: ', error);
      }
    },

    triggerErrorToast() {
      this.$refs.toasts.createToast(
        `
          <h3 class="title">Tente novamente mais tarde</h3>
          <p class="subtitle">Encontramos um erro inesperado e não conseguimos recuperar as unidades. Aguarde alguns instantes e tente mais uma vez.</p>
        `,
        'danger'
      );
    },

    setLogos(companies) {
      companies.forEach((company) => {
        this.logos[company.id] = {
          url: `/images/map/shipping-companies/${company.name.replace(/\s/g, '').toLowerCase()}.png`,
          scaledSize: new google.maps.Size(158, 83),
        };
      });
    },

    getMarkerContent(unit) {
      return `
        <div class="me-map__marker">
          <p>
            <b>Agência</b>: ${unit.name}
          </p>
          <p>
            <b>Cidade</b>: ${unit.address.city} / ${unit.address.state_abbr}
          </p>
          <p>
            <b>Endereço</b>: ${unit.address.address}
            ${unit.address.number ? `, ${unit.address.number}` : ''}
            ${unit.address.complement ? `, ${unit.address.complement}` : ''}
            ${unit.address.district ? ` - ${unit.address.district}` : ''}
          </p>
          ${unit.email ? `<p><b>E-mail</b>: ${unit.email}</p>` : ''}
          ${unit.notes ? `<p><b>Obs</b>: ${unit.notes}</p>` : ''}
        </div>
      `;
    },

    filter(event) {
      const value = typeof event === 'string' ? event : event.target.value;

      this.company = value;
    },

    async updateLimit(event) {
      this.limit = event.target.value;
    },

    createSearch() {
      const search = document.createElement('input');

      search.className = 'me-map__control';
      search.placeholder = "Insira sua localização";

      const box = new google.maps.places.SearchBox(search);

      this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(search);

      this.map.addListener('bounds_changed', () => {
        box.setBounds(this.map.getBounds());
      });

      box.addListener('places_changed', async () => {
        try {
          this.loading = true;

          const places = box.getPlaces();

          if (!places.length) return;

          const bounds = new google.maps.LatLngBounds();

          const [place] = places;

          this.center.setPosition(place.geometry.location);

          if (place.geometry.viewport) bounds.union(place.geometry.viewport);
          else bounds.extend(place.geometry.location);

          this.map.fitBounds(bounds);

          this.position = {
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          };

          await this.$nextTick();

          await this.getUnits();
        } catch (error) {
          this.triggerErrorToast();
        } finally {
          this.loading = false;
        }
      });
    },

    createFilter() {
      const select = document.createElement('select');

      select.className = 'me-map__control';

      const fragment = new DocumentFragment();

      const all = document.createElement('option');

      all.innerHTML = 'Todas as companhias';
      all.selected = true;
      all.value = '';

      fragment.appendChild(all);

      this.companies.forEach((company) => {
        const option = document.createElement('option');

        option.innerHTML = company.name;
        option.value = company.id;

        fragment.appendChild(option);
      });

      const partner = document.createElement('option');

      partner.innerHTML = 'Ponto Parceiro';
      partner.value = 'partner';

      fragment.appendChild(partner);

      select.appendChild(fragment);
      select.addEventListener('change', this.filter);

      this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(select);
    },

    createLimit() {
      const select = document.createElement('select');

      select.className = 'me-map__control';

      const fragment = new DocumentFragment();

      const values = [10, 50, 100, 200, 500, 1000];

      values.forEach((value) => {
        const option = document.createElement('option');

        option.innerHTML = `${value} Unidades`;

        option.value = value;

        if (value === this.limit) {
          option.selected = true;
        }

        fragment.appendChild(option);
      });

      select.appendChild(fragment);

      select.addEventListener('change', this.updateLimit);

      this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(select);

      const tooltip = document.createElement('div');

      tooltip.className = 'me-map__tooltip';
      tooltip.innerText = 'Selecione a quantidade de unidades próximas de você.';

      document.body.appendChild(tooltip);

      select.addEventListener('mouseenter', () => {
        tooltip.style.display = 'block';
      });

      select.addEventListener('mousemove', (event) => {
        tooltip.style.left = `${event.pageX + 10}px`;
        tooltip.style.top = `${event.pageY + 10}px`;
      });

      select.addEventListener('mouseleave', () => {
        tooltip.style.display = 'none';
      });
    },

    createControls() {
      this.createSearch();
      this.createFilter();
      this.createLimit();
    },

    async getPosition() {
      return new Promise((resolve) => {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(async ({ coords }) => {
            this.position = {
              latitude: coords.latitude,
              longitude: coords.longitude,
            };

            await this.$nextTick();

            resolve();
          }, resolve);
        } else {
          resolve();
        }
      });
    },

    async getCompanies() {
      const { data, error } = await $get('/api/v2/me/shipment/services', {
        params: {
          status: 'available',
        },
      });

      if (error) return;

      const companies = uniqBy(data, 'company.id').map(({ company }) => company);

      this.companies = companies;

      this.setLogos(companies);
    },

    async getUnits() {
      const { data, error } = await $get('/api/v2/me/shipment/agencies', {
        params: {
          sanitize: false,
          status: 'available',
          distanceSearch: 100000,
          limit: 1000,
          ...this.position,
        },
      });

      if (error) return;

      this.units = data.map((unit) => ({
        ...unit,
        address: capitalizeObject(unit.address),
      }));
    },

    async createMarkers() {
      this.markers = this.filteredUnits.map((unit) => {
        const [company] = unit.companies;
        const [longitude, latitude] = unit.location.coordinates;
        const pudosIcon = unit.pudos_company === 2 ? this.$options.static.pegaki : this.$options.static.melhor_envio;
        const marker = new google.maps.Marker({
          position: new google.maps.LatLng(latitude, longitude),
          title: 'Clique duas vezes para dar zoom',
          icon: unit.type === 'normal'
            ? this.logos[company]
            : pudosIcon,
          map: this.map,
        });

        const info = new google.maps.InfoWindow({
          content: this.getMarkerContent(unit),
        });

        marker.addListener('click', () => {
          info.open(this.map, marker);
        });

        return marker;
      });

      await this.$nextTick();
    },

    createCluster() {
      const renderer = {
        render: ({ count, position }) => new google.maps.Marker({
          label: {
            text: String(count),
            color: '#ffffff',
            fontSize: '12px',
          },
          icon: this.$options.static.cluster,
          position,
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        }),
      };

      this.cluster = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        renderer,
      });
    },

    async createMap() {
      this.map = new google.maps.Map(this.$refs.map, {
        center: {
          lat: this.position.latitude,
          lng: this.position.longitude,
        },
        zoom: 10,
        zoomControlOptions: {
          position: google.maps.ControlPosition.LEFT_BOTTOM,
        },
        streetViewControlOptions: {
          position: google.maps.ControlPosition.LEFT_BOTTOM,
        },
      });

      await this.$nextTick();

      this.center = new google.maps.Marker({
        position: new google.maps.LatLng(this.position.latitude, this.position.longitude),
        title: 'Você está aqui',
        icon: this.$options.static.person,
        map: this.map,
      });

      this.createControls();

      await this.createMarkers();

      this.createCluster();
    },

    async init() {
      try {
        await this.getPosition();

        await Promise.all([
          this.getCompanies(),
          this.getUnits(),
        ]);

        await this.createMap();
      } catch (error) {
        this.triggerErrorToast();
      } finally {
        this.loading = false;
      }
    },
  },

  static: {
    agency: '/images/map/pin-agency.png',
    person: '/images/map/pin-person.png',
    melhor_envio: '/images/map/pin-melhor-envio.png',
    pegaki: '/images/map/pin-pegaki.png',
    cluster: '/images/map/pin-cluster.png',
  },
};
